import { AlertCircle, RefreshCcw } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { extractApiErrorMessage } from '@/lib/api-error';

/**
 * Minimum TanStack Query state surface this boundary depends on. Kept as a
 * structural type (not `UseQueryResult`) so the boundary also works with
 * partial mocks in tests and with composite "many queries rolled up" objects.
 */
export interface QueryStateLike<T> {
  data: T | undefined | null;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  refetch?: () => unknown;
}

interface Props<T> {
  query: QueryStateLike<T>;
  children: (data: NonNullable<T>) => ReactNode;
  /** Falsy `data` is treated as empty. Override for non-list data or when the
   *  concept of "empty" is inappropriate. */
  isEmpty?: (data: NonNullable<T>) => boolean;
  /** Replaces the default skeleton. */
  loadingFallback?: ReactNode;
  /** Error copy (supplements whatever the API error object carries). */
  errorTitle?: string;
  /** Fallback when message field is missing from the error. */
  errorFallback?: string;
  /** Empty-state render. Defaults to a muted line that reads `emptyMessage`. */
  emptyFallback?: ReactNode;
  emptyMessage?: string;
}

const DEFAULT_IS_EMPTY = <T,>(data: NonNullable<T>): boolean =>
  Array.isArray(data) && data.length === 0;

/**
 * Renders one of four states for a TanStack query:
 *
 *   loading  → skeleton (replaceable)
 *   error    → AlertCircle card with message + optional Retry button
 *   empty    → muted "no results" line (replaceable)
 *   success  → `children(data)` with `data` narrowed to `NonNullable<T>`
 *
 * Use instead of inlined `if (isLoading) ... else if (data.length === 0) ...`
 * idioms — a journey-audit Pattern A P1 was that most surfaces silently render
 * fetch errors as "empty", misinforming the user. This closes that on the
 * wrapper side.
 */
export function QueryStateBoundary<T>({
  query,
  children,
  isEmpty = DEFAULT_IS_EMPTY,
  loadingFallback,
  errorTitle = 'Something went wrong',
  errorFallback,
  emptyFallback,
  emptyMessage = 'No results found.',
}: Props<T>) {
  if (query.isLoading) {
    return (
      <>
        {loadingFallback ?? (
          <div className="space-y-2 p-4" data-slot="query-boundary-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}
      </>
    );
  }

  if (query.isError) {
    const message = extractApiErrorMessage(query.error, errorFallback);
    return (
      <div
        role="alert"
        data-slot="query-boundary-error"
        className="flex flex-col items-start gap-3 rounded-xl border border-[var(--border-error)] bg-[var(--status-red-tint)] p-4"
      >
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-[var(--status-red-text)]" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-[var(--text-primary)]">{errorTitle}</p>
            <p className="text-sm text-[var(--text-secondary)]">{message}</p>
          </div>
        </div>
        {query.refetch && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void query.refetch?.()}
            className="gap-1.5"
          >
            <RefreshCcw className="size-3.5" />
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (query.data === undefined || query.data === null) {
    // Query finished without data and without error — render as empty rather
    // than crashing the children function.
    return (
      <>
        {emptyFallback ?? (
          <p
            data-slot="query-boundary-empty"
            className="text-sm text-[var(--text-tertiary)] p-4 text-center"
          >
            {emptyMessage}
          </p>
        )}
      </>
    );
  }

  const data = query.data as NonNullable<T>;
  if (isEmpty(data)) {
    return (
      <>
        {emptyFallback ?? (
          <p
            data-slot="query-boundary-empty"
            className="text-sm text-[var(--text-tertiary)] p-4 text-center"
          >
            {emptyMessage}
          </p>
        )}
      </>
    );
  }

  return <>{children(data)}</>;
}
